import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { DateFilter } from "components/Filters/DateFilter";
import { ProductChip } from "components/product/ProductChip";

import SmallTax from "static/images/SmallTax.svg";
import SmallBooks from "static/images/SmallBooks.svg";
import SmalMailroom from "static/images/SmallMailroom.svg";
import SmallSalesTax from "static/images/SmallSalesTax.svg";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { formatDate } from "utils/formatDate";
import { BOOKS, MAILROOM, SALES_TAX, TAX } from "constants/apps";
import { Button } from "components/DesignSystem/Button/Button";
import { Funnel } from "components/icons/Funnel";

const APPS = "APPS";

const FILTER_TYPES = {
  INVOICE_TYPE: "INVOICE_TYPE",
  SEASON: "SEASON",
  STATUS: "STATUS",
  DATE_RANGE: "DATE_RANGE",
  APPS: "APPS",
};

export type InvoiceFilterType = {
  STATUS: string[];
  INVOICE_TYPE: string[];
  SEASONS: string[];
  START_DATE: string;
  END_DATE: string;
  SELECT_PERIOD: string;
  APPS: string[];
  SORT_BY: string;
};

export type FilterProps = {
  values: InvoiceFilterType;
  updateFilter: <S extends keyof InvoiceFilterType>(
    name: S,
    newValue: InvoiceFilterType[S]
  ) => void;
};

const INVOICE_TYPE = [
  { label: "One time", value: "ONE_TIME" },
  { label: "Recurring", value: "RECURRING" },
  { label: "Monthly bookkeeping", value: "MONTHLY_BOOKKEEPING" },
  { label: "Balancing payment", value: "BALANCING_PAYMENT" },
];

const SEASON_OPTIONS = [
  { label: "2025", value: "2025" },
  { label: "2024", value: "2024" },
  { label: "2023", value: "2023" },
  { label: "2022", value: "2022" },
  { label: "2021", value: "2021" },
  { label: "2020", value: "2020" },
];
export const STATUS_OPTIONS_MAP = {
  UNPAID: ["PENDING", "IN_PROGRESS", "WORK_IN_PROGRESS"],
  PAID: ["PAID"],
  FULL_REFUND: ["FULL_REFUND"],
  PARTIAL_REFUND: ["PARTIAL_REFUND"],
  LATE_FEE: ["LATE_FEE"],
  AUTOPAY_INVOICE: ["AUTOPAY_INVOICE"],
  ON_HOLD_INVOICE: ["ON_HOLD_INVOICE"],
};

export const STATUS_OPTIONS = [
  { label: "Unpaid invoices", value: "UNPAID" },
  { label: "Paid invoices", value: "PAID" },
  { label: "Full refund invoices", value: "FULL_REFUND" },
  { label: "Partial refund invoices", value: "PARTIAL_REFUND" },
  { label: "Invoices with late fees", value: "LATE_FEE" },
  { label: "Invoices with autopay", value: "AUTOPAY_INVOICE" },
  { label: "On hold invoices", value: "ON_HOLD_INVOICE" },
];

const AppliedFilters = ({ values, updateFilter }: FilterProps) => {
  const isBooksActive = values.APPS?.includes(BOOKS);
  const isTaxActive = values.APPS?.includes(TAX);
  const isMailroomActive = values.APPS?.includes(MAILROOM);
  const isSalesTaxActive = values.APPS?.includes(SALES_TAX);

  const handleProudctClick = (type: keyof InvoiceFilterType, value: string) => {
    const currentSelection = values[type] || [];
    const isSelected = currentSelection.includes(value);

    if (isSelected && Array.isArray(currentSelection)) {
      updateFilter(
        type,
        currentSelection.filter((item) => item !== value)
      );
    } else {
      updateFilter(type, [...currentSelection, value]);
    }
  };

  return (
    <>
      {!values.STATUS.some((e) => e === "DRAFT") && (
        <>
          <ProductChip
            onClick={() => handleProudctClick(APPS, BOOKS)}
            isActive={isBooksActive}
            onClose={() => handleProudctClick(APPS, BOOKS)}
            icon={<img src={SmallBooks} alt="SmallBooks" />}
          >
            Books
          </ProductChip>
          <ProductChip
            onClick={() => handleProudctClick(APPS, TAX)}
            isActive={isTaxActive}
            onClose={() => handleProudctClick(APPS, TAX)}
            icon={<img src={SmallTax} alt="SmallTax" />}
          >
            Tax
          </ProductChip>
          <ProductChip
            onClick={() => handleProudctClick(APPS, MAILROOM)}
            isActive={isMailroomActive}
            onClose={() => handleProudctClick(APPS, MAILROOM)}
            icon={<img src={SmalMailroom} alt="SmalMailroom" />}
          >
            Mailroom
          </ProductChip>
          <ProductChip
            onClick={() => handleProudctClick(APPS, SALES_TAX)}
            isActive={isSalesTaxActive}
            onClose={() => handleProudctClick(APPS, SALES_TAX)}
            icon={<img src={SmallSalesTax} alt="SmallSalesTax" />}
          >
            Sales Tax
          </ProductChip>

          {values.INVOICE_TYPE?.length > 0 && (
            <Chip
              isActive
              filterType="INVOICE_TYPE"
              onRemove={() => updateFilter("INVOICE_TYPE", [])}
            >
              Invoice type ({values.INVOICE_TYPE.length})
            </Chip>
          )}
          {values.STATUS?.length > 0 && (
            <Chip
              isActive
              filterType="STATUS"
              onRemove={() => updateFilter("STATUS", [])}
            >
              Status ({values.STATUS.length})
            </Chip>
          )}
          {values.SEASONS?.length > 0 && (
            <Chip
              isActive
              filterType="SEASONS"
              onRemove={() => updateFilter("SEASONS", [])}
            >
              Season ({values.SEASONS.length})
            </Chip>
          )}
          {values.START_DATE && (
            <Chip
              isActive
              onRemove={() => {
                updateFilter("START_DATE", "");
                updateFilter("END_DATE", "");
              }}
              filterType="journalEntryDate"
            >
              From: {formatDate(values.START_DATE)} To:{" "}
              {formatDate(values.END_DATE)}
            </Chip>
          )}
        </>
      )}
    </>
  );
};

export const InvoiceFilter = ({ values, updateFilter }: FilterProps) => {
  return (
    <Filter.Root
      defaultValue={FILTER_TYPES.INVOICE_TYPE}
      withWrapper={false}
      hideDivider
      capsule={<AppliedFilters updateFilter={updateFilter} values={values} />}
      trigger={
        <Button size="xsmall" customType="icon">
          <div className="t-flex t-items-center t-text-text-30 t-gap-1">
            <Funnel />
          </div>
        </Button>
      }
    >
      <Filter.Portal size="small">
        <Filter.List>
          <Filter.ListItem value={FILTER_TYPES.INVOICE_TYPE}>
            Invoice type
          </Filter.ListItem>
          <Filter.ListItem value={FILTER_TYPES.SEASON}>Season</Filter.ListItem>
          <Filter.ListItem value={FILTER_TYPES.STATUS}>Status</Filter.ListItem>
          <Filter.ListItem value={FILTER_TYPES.DATE_RANGE}>
            Date range
          </Filter.ListItem>
        </Filter.List>
        <Filter.Body block value={FILTER_TYPES.SEASON}>
          <MultiSelectFilter
            selected={values.SEASONS || []}
            isSelectAll={false}
            isSearchable={false}
            options={SEASON_OPTIONS}
            onChange={(value) => {
              updateFilter("SEASONS", value);
            }}
          />
        </Filter.Body>
        <Filter.Body value={FILTER_TYPES.INVOICE_TYPE}>
          <MultiSelectFilter
            selected={values.INVOICE_TYPE || []}
            isSelectAll={false}
            isSearchable={false}
            options={INVOICE_TYPE}
            onChange={(value) => {
              updateFilter("INVOICE_TYPE", value);
            }}
          />
        </Filter.Body>
        <Filter.Body value={FILTER_TYPES.STATUS}>
          <MultiSelectFilter
            selected={values.STATUS || []}
            isSelectAll={false}
            isSearchable={false}
            options={STATUS_OPTIONS}
            onChange={(value) => {
              updateFilter("STATUS", value);
            }}
          />
        </Filter.Body>
        <Filter.Body value={FILTER_TYPES.DATE_RANGE} block>
          <DateFilter values={values} updateFilter={updateFilter} />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
